import React from 'react';
import styled from 'styled-components';
import { ApolloProvider } from '@apollo/client';
import env from 'bench-environment';
import { useV2Query } from 'utils';
import graphqlClient from '../graphqlClient';
// Deep imports rather than `from 'billing-web-app'`: that package's index pulls in
// BillingApp, which imports `graphqlClient` from this package. Reaching straight for
// the query/type modules keeps the dependency one-way. Same reason as ClientTaxDetails.
import {
  CONTRACTS_FOR_CLIENT_QUERY,
  ContractsForClientQueryData,
  ContractsForClientQueryVariables,
  ContractWithContents,
} from '../../billing-web-app/queries';
import {
  ContractStatus,
  PAYMENT_CADENCE_LABEL,
  PRODUCT_LINE_LABEL,
} from '../../billing-web-app/types';
import {
  computeLine,
  currencyFormatter,
  formatDate,
  formatDateShort,
} from '../../billing-web-app/components/ContractDetailsPage/contractLineUtils';
import { quantityFormatter } from '../../billing-web-app/invoiceLineMath';

const STATUS_LABEL: Record<ContractStatus, string> = {
  DRAFT: 'Draft',
  PENDING_SIGNATURE: 'Pending signature',
  SIGNED: 'Signed',
  ACTIVE: 'Active',
  DECLINED: 'Declined',
  CANCELLED: 'Cancelled',
  PAUSED: 'Paused',
};

/**
 * Loud enough that an operator scanning the Billing card cannot miss a client
 * who is not currently under contract — this is the state that means "no
 * service is being billed for".
 */
const NoContractBanner = styled.div`
  border: 2px solid #e8833a;
  border-radius: 4px;
  background-color: #fdf0e1;
  color: #8a4a11;
  padding: 1em 1.25em;
  margin-bottom: 1.5em;

  .headline {
    font-weight: 600;
    font-size: 1.1em;
    margin-bottom: 0.5em;
  }

  ul {
    list-style: disc;
    margin: 0.5em 0 0 1.5em;
  }

  li {
    margin-bottom: 0.25em;
  }
`;

const Note = styled.div`
  margin-bottom: 1em;
  color: #8a4a11;
  font-weight: 600;
`;

const Message = styled.div`
  margin-bottom: 1em;
`;

const LinesHeading = styled.div`
  font-size: 16px;
  font-weight: bold;
  padding-bottom: 16px;
`;

const TotalRow = styled.div`
  padding-top: 0.75em;
  text-align: right;
  font-weight: 600;
`;

interface ReadOnlyFieldProps {
  id: string;
  label: string;
  value: string;
  /** When given, the field renders with the card's external-link affordance and opens this URL. */
  href?: string;
}

/**
 * Mirrors the DOM that the Angular `bl-input` directive emits for a read-only
 * field, so these rows are indistinguishable from the hand-written ones in the
 * surrounding card. `non-empty` is the class the directive's link function
 * applies to float the label above a filled input.
 *
 * The label element is deliberately empty: the card's stylesheet paints its text
 * from `content: attr(placeholder)`. That leaves nothing for a screen reader to
 * announce, so the input carries an `aria-label` of its own.
 */
const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ id, label, value, href }) => (
  <div className="half">
    <div className={href ? 'bl-input link' : 'bl-input'}>
      <input
        id={id}
        type="text"
        className="non-empty"
        aria-label={label}
        value={value}
        readOnly
        onClick={href ? () => window.open(href, '_blank', 'noopener') : undefined}
      />
      <label htmlFor={id} {...({ placeholder: label, alt: label } as any)} />
    </div>
  </div>
);

/** Deep link to a contract in the Billing app, which is served from the dashboard host. */
function contractUrl(contractId: string): string {
  return `${env.location.dashboard}/billing/contracts/${contractId}`;
}

/** Deep link to the Billing app's contract list, scoped to one client. */
function clientContractsUrl(clientId: string): string {
  return `${env.location.dashboard}/billing/contracts?clientId=${encodeURIComponent(clientId)}`;
}

/** Sum of the net value of every line on the contract — the same figure the Billing app shows. */
function contractValue(contract: ContractWithContents): number {
  return contract.contents.reduce(
    (total, content) => total + computeLine(content, content.product).netSubtotal,
    0,
  );
}

const ContractLines: React.FC<{ contract: ContractWithContents }> = ({ contract }) => {
  if (contract.contents.length === 0) {
    return <Message>This contract has no product lines.</Message>;
  }

  return (
    <div className="bl-card_text-block">
      <LinesHeading>Products on this contract</LinesHeading>
      <table className="bl-card_table">
        <thead>
          <tr>
            <th>Product</th>
            <th>Line</th>
            <th>Cadence</th>
            <th>Term</th>
            <th>Qty</th>
            <th>Payments</th>
            <th>Discount</th>
            <th>Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {contract.contents.map(content => {
            const line = computeLine(content, content.product);
            return (
              <tr key={content.id}>
                <td title={content.product.sku}>{content.product.displayName}</td>
                <td>{PRODUCT_LINE_LABEL[content.product.productLine]}</td>
                <td>{PAYMENT_CADENCE_LABEL[content.product.cadence] ?? content.product.cadence}</td>
                <td>
                  {formatDateShort(content.startDate)} &rarr; {formatDateShort(content.endDate)}
                </td>
                <td>{quantityFormatter.format(content.quantity)}</td>
                <td>{line.numPayments}</td>
                <td>{line.discountLabel}</td>
                <td>{currencyFormatter.format(line.netSubtotal)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <TotalRow>Contract total {currencyFormatter.format(contractValue(contract))}</TotalRow>
    </div>
  );
};

const ActiveContract: React.FC<{ contract: ContractWithContents; extraActiveCount: number }> = ({
  contract,
  extraActiveCount,
}) => (
  <>
    {extraActiveCount > 0 && (
      <Note>
        {extraActiveCount + 1} contracts are marked active for this client — showing the most
        recently created one.
      </Note>
    )}
    <div className="bl-card_input-block">
      <ReadOnlyField
        id="billing-contract-name"
        label="Active Contract"
        value={contract.nickname || contract.client.businessName}
        href={contractUrl(contract.id)}
      />
      <ReadOnlyField
        id="billing-contract-status"
        label="Contract Status"
        value={STATUS_LABEL[contract.status]}
      />
    </div>
    <div className="bl-card_input-block">
      <ReadOnlyField
        id="billing-contract-start"
        label="Contract Start Date"
        value={formatDate(contract.startDate)}
      />
      <ReadOnlyField
        id="billing-contract-end"
        label="Contract End Date"
        value={formatDate(contract.endDate)}
      />
    </div>
    <div className="bl-card_input-block">
      <ReadOnlyField
        id="billing-contract-value"
        label="Contract Value"
        value={currencyFormatter.format(contractValue(contract))}
      />
      <ReadOnlyField
        id="billing-contract-created-by"
        label="Contract Created By"
        value={contract.createdBy || '—'}
      />
    </div>
    <ContractLines contract={contract} />
  </>
);

const NoActiveContract: React.FC<{ clientId: string; contracts: ContractWithContents[] }> = ({
  clientId,
  contracts,
}) => (
  <NoContractBanner>
    <div className="headline">No active contract</div>
    {contracts.length === 0 ? (
      <div>This client has no contracts in the Billing app.</div>
    ) : (
      <>
        <div>
          This client has no contract in the Active state. The{' '}
          {contracts.length === 1 ? 'contract on file is' : 'contracts on file are'}:
        </div>
        <ul>
          {contracts.map(contract => (
            <li key={contract.id}>
              <a href={contractUrl(contract.id)} target="_blank" rel="noopener noreferrer">
                {contract.nickname || contract.client.businessName}
              </a>{' '}
              — {STATUS_LABEL[contract.status]} ({formatDateShort(contract.startDate)} &rarr;{' '}
              {formatDateShort(contract.endDate)})
            </li>
          ))}
        </ul>
      </>
    )}
    <div style={{ marginTop: '0.75em' }}>
      <a href={clientContractsUrl(clientId)} target="_blank" rel="noopener noreferrer">
        Manage this client&rsquo;s contracts in Billing
      </a>
    </div>
  </NoContractBanner>
);

interface ClientBillingContractProps {
  clientId: string;
}

const ClientBillingContractContent: React.FC<ClientBillingContractProps> = ({ clientId }) => {
  const { data, loading, error } = useV2Query<
    ContractsForClientQueryData,
    ContractsForClientQueryVariables
  >(CONTRACTS_FOR_CLIENT_QUERY, {
    variables: { filter: { clientIds: [String(clientId)] }, limit: 200 },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  // Guard on the payload, not on `data`: errorPolicy 'all' resolves with a `data`
  // object whose fields are null alongside `error`, so `!data` never catches a
  // failed fetch and we would claim the client has no contract.
  const items = data?.contracts?.items;

  if (loading && !items) return <Message>Loading contract…</Message>;
  if (error && !items) return <Message>Failed to load contracts: {error.message}</Message>;

  // Server order is createdAt DESC, so the first match is always the newest.
  const contracts = items ?? [];
  const active = contracts.filter(contract => contract.status === 'ACTIVE');

  if (active.length === 0) {
    return <NoActiveContract clientId={String(clientId)} contracts={contracts} />;
  }

  return <ActiveContract contract={active[0]} extraActiveCount={active.length - 1} />;
};

/**
 * The Billing section of the BK Tools client details page: the client's active
 * contract and the products sold under it, read from the Billing app's GraphQL
 * API.
 *
 * Rendered inside the legacy Angular card via the `client-billing-contract`
 * directive, so it reuses the card's own `bl-input` / `bl-card_table` classes
 * instead of the design system.
 */
export const ClientBillingContract: React.FC<ClientBillingContractProps> = ({ clientId }) => (
  <ApolloProvider client={graphqlClient}>
    <ClientBillingContractContent clientId={clientId} />
  </ApolloProvider>
);

export default ClientBillingContract;
